import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { blurRadii, check, hasLongTravel } from '../skillUsage';

export const motionBlurStreakSkill: CodeoSkill = {
    name: SKILL_NAMES.motionBlurStreak,
    kind: 'codeo',
    description:
        'Smear an element along its direction of travel while it is fastest, then resolve — the ' +
        'blur that makes a quick move read as weight and speed rather than as a jump.',
    checkLooksFor:
        'a blur of at least 8px, on something travelling 300px or more — a named distance counts only when written in capitals',
    documentUsesSkill: check(
        (script) => blurRadii(script).some((radius) => radius >= 8) && hasLongTravel(script),
    ),
    instructions: `# Motion Blur Streak

An element blurs along its axis of travel at the moment it is moving fastest, and sharpens as it settles. Real cameras do this, so its absence is what makes fast digital motion look weightless — a card that crosses the frame in a fifth of a second and stays perfectly sharp reads as teleporting.

It is a modifier rather than a technique on its own: add it to a move that already exists and is already fast.

## Markup

\`\`\`html
<div class="absolute inset-0 flex flex-col justify-center items-start gap-10 p-24">
  <div id="streak-card" class="flex items-center justify-center" data-l5-allow-offscreen style="width: 420px; height: 280px; border-radius: 24px; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 54px; font-weight: 800; color: var(--l5-color-1, #101820)">Fast</div>
</div>
\`\`\`

## Timeline code

The blur ramps up and back down inside the move, peaking where the velocity peaks. On a horizontal move, blur only the horizontal axis.

**Speed is real seconds.** The timeline is authored to the scene's own length, so \`duration: 0.48\` is just under half a second and the move's speed is the distance over it. The example below covers 1180px in 0.48s — roughly 2,500 px/s, fast enough that a blur has something to describe. Spread the same travel over 3s and it crawls at 390 px/s, where a blur reads as a smudge rather than as speed. Keep a streak's travel in the 0.3–0.6s range.

\`\`\`js
const tl = gsap.timeline({ paused: true });
// TRAVEL has to clear the frame on BOTH sides, and the two sides ask for
// different numbers because the card is rarely centred. With the card at
// left: 750, width: 420, in a 1920 frame:
//   entry — the right edge must pass x = 0,    so TRAVEL >= left + width  = 1170
//   exit  — the left edge must pass x = 1920,  so TRAVEL >= 1920 - left   = 1170
// Take the larger of the two and round up. Getting this wrong is invisible in
// the code and glaring on screen: a slab of the card parked against one edge.
const SCENE_SECONDS = 9.6; // this scene's duration
const TRAVEL = 1180;
const SPAN = 0.48;        // just under half a second of travel — see the note above
const ENTER = 1.35;
const EXIT = 6.5;

// The CSS left holds the RESTING position; x is only ever the offset from it. Encoding
// the off-screen start in both (left: -620 and x: -620) stacks the two, and the card
// never makes it onto the frame at all.
gsap.set('#streak-card', { x: -TRAVEL, filter: 'blur(0px)' });

// In, fast, blurred.
tl.to('#streak-card', { x: 0, duration: SPAN, ease: 'power3.out' }, ENTER);
tl.to('#streak-card', { filter: 'blur(14px)', duration: SPAN / 2, ease: 'power2.out' }, ENTER - SPAN / 2);
tl.to('#streak-card', { filter: 'blur(0px)', duration: SPAN / 2, ease: 'power2.out' }, ENTER + SPAN / 2);

// It rests, fully on screen — this hold is what the blur exists to contrast against.

// Out the far side, same treatment.
tl.to('#streak-card', { x: TRAVEL, duration: SPAN, ease: 'power3.in' }, EXIT);
tl.to('#streak-card', { filter: 'blur(14px)', duration: SPAN / 2, ease: 'power2.in' }, EXIT);
tl.to('#streak-card', { filter: 'blur(0px)', duration: SPAN / 2, ease: 'power2.out' }, EXIT + SPAN / 2);

// The timeline runs the scene's full length, so the card holds after the streak lands.
tl.set({}, {}, SCENE_SECONDS);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Directional smear**: a CSS \`blur()\` is isotropic. For a true directional streak, author an inline SVG \`feGaussianBlur\`, apply it with \`filter: url(#streak)\` in the *markup*, and tween the filter's own \`stdDeviation\` attribute from \`"0 0"\` to \`"14 0"\` — a numeric attribute interpolates, whereas swapping the \`filter\` property to a \`url()\` reference does not (see Pitfalls).
- **Blur amount**: roughly the distance the element covers in one frame. 1180px over 0.48s at 30fps is about 82px per frame, so 12–18px of blur reads right; far more looks like fog.
- **Match the ease**: the blur peak must sit on the velocity peak. With \`power3.inOut\` that is the midpoint; with \`power3.out\` it is near the start.
- **Ghosts instead**: for a stylized rather than photographic smear, use \`${SKILL_NAMES.echoTrailGhosts}\`.
- **On the exit**: blurring an element as it leaves is cheaper than blurring an arrival, because nobody needs to read it on the way out.
- **Whole-frame version**: the same ramp applied to a full-frame pan is \`${SKILL_NAMES.whipPanCut}\`.

## Pitfalls

- Tween the full \`filter\` string with units in both endpoints; a bare number will not interpolate.
- If the element carries other filters, repeat them in both endpoints or they are dropped for the duration of the tween.
- The blur must return to exactly \`blur(0px)\`. A residual blur on a settled element reads as a rendering fault.
- **Never tween \`filter\` from a \`blur()\` to a \`url(#…)\` reference.** GSAP cannot interpolate between a filter function and a filter reference, so the smear appears discretely instead of ramping — which removes the one thing the technique is for. If you want a directional streak, keep tweening \`blur()\` and accept it is isotropic, or drive an SVG filter's own \`stdDeviation\` as a numeric value.
- **Judge the speed in seconds.** The timeline runs in the scene's own seconds, so \`SPAN\` is the real duration of the move and the rate is simply \`TRAVEL ÷ SPAN\` — sanity-check that it lands north of about 1,500 px/s. Spreading the same travel over two or three seconds is what turns a streak into a crawl.
- Don't blur a slow move. Under about 400px of travel there is nothing to smear and the element just looks soft.
- **Keep the resting position in \`left\` and use \`x\` purely as travel.** Encoding the off-screen start in both — a negative \`left\` *and* a negative \`x\` — stacks the two offsets, so the element enters from twice as far away and rests half off the frame. It looks like a clipping bug rather than a fast move.
- **Check \`TRAVEL\` against the element's own box before you trust it.** \`x: -TRAVEL\` only hides the element if \`TRAVEL >= left + width\`, and \`x: +TRAVEL\` only clears the far side if \`TRAVEL >= frameWidth - left\`; one \`TRAVEL\` serves both ends, so it must satisfy the larger. A round-looking number that fails either test leaves a slab of the element parked against that edge for every frame outside the move — which reads as a broken layout, not as a fast entrance. An enclosing \`scale\` on the stage does not rescue it; a 1.02 push moves the frame edge by a few pixels, not a few hundred.
- Give it somewhere to rest. A card that enters blurred and immediately leaves never resolves, and the sharp held state is what the smear is measured against.
- Never blur an element the viewer is meant to read while it travels. Blur the move, not the message.
- Blur is the most expensive filter to rasterize; keeping it to a fraction of a second on one element is what makes it affordable.
- Blur extends past the element's box, so a streaking element near the frame edge may need \`data-l5-allow-offscreen\`.`,
};
